import type { QueryClient } from '@tanstack/react-query';
import { generateUUID } from '@/lib/utils';
import {
  invalidateProjectAnnotationCounts,
  invalidateProjectAnnotationList,
  type ProjectAnnotationListScope,
} from '@/hooks/api/useProjectAnnotations';
import type { RealtimeContext, RealtimeEvent } from '@/types/realtime';

const ANNOTATION_REALTIME_BATCH_DELAY_MS = 120;

interface PendingAnnotationInvalidation {
  queryClient: QueryClient;
  projectId: string;
  screenplayId: string;
  events: Set<string>;
  entityKeys: Set<string>;
  timeoutId: ReturnType<typeof setTimeout> | null;
}

const pendingAnnotationInvalidations = new Map<string, PendingAnnotationInvalidation>();

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function getPendingInvalidationKey(projectId: string, screenplayId: string): string {
  return `${projectId}:${screenplayId}`;
}

function getAnnotationEntityEventKey(event: RealtimeEvent): string {
  const annotationId = readString(event.payload.annotationId);
  const actorUserId = readString(event.payload.actorUserId);
  const occurredAt = readString(event.payload.occurredAt);
  if (annotationId && actorUserId) {
    return `${event.event}:${annotationId}:${actorUserId}`;
  }
  return `${event.event}:${event.eventId ?? occurredAt ?? generateUUID()}`;
}

function getListScopesForAnnotationEvents(
  events: ReadonlySet<string>
): readonly ProjectAnnotationListScope[] {
  const hasOnlyReplyEvents = [...events].every((eventName) => eventName === 'reply_created');
  if (hasOnlyReplyEvents) {
    return ['panel', 'target', 'preview-target'];
  }
  return ['panel', 'target', 'preview-target', 'parse-flow-aggregate', 'actionable-index'];
}

function flushPendingAnnotationInvalidation(key: string): void {
  const pending = pendingAnnotationInvalidations.get(key);
  if (!pending) return;
  pendingAnnotationInvalidations.delete(key);
  if (pending.timeoutId) {
    clearTimeout(pending.timeoutId);
  }
  invalidateProjectAnnotationList(pending.queryClient, pending.projectId, pending.screenplayId, {
    scopes: getListScopesForAnnotationEvents(pending.events),
  });
  invalidateProjectAnnotationCounts(pending.queryClient, pending.projectId, pending.screenplayId);
}

function scheduleAnnotationRealtimeInvalidation(
  queryClient: QueryClient,
  event: RealtimeEvent,
  projectId: string,
  screenplayId: string
): void {
  const key = getPendingInvalidationKey(projectId, screenplayId);
  const entityKey = getAnnotationEntityEventKey(event);
  const pending = pendingAnnotationInvalidations.get(key);

  if (pending) {
    if (!pending.entityKeys.has(entityKey)) {
      pending.entityKeys.add(entityKey);
      pending.events.add(event.event);
    }
    return;
  }

  const nextPending: PendingAnnotationInvalidation = {
    queryClient,
    projectId,
    screenplayId,
    events: new Set([event.event]),
    entityKeys: new Set([entityKey]),
    timeoutId: null,
  };
  nextPending.timeoutId = setTimeout(() => {
    flushPendingAnnotationInvalidation(key);
  }, ANNOTATION_REALTIME_BATCH_DELAY_MS);
  pendingAnnotationInvalidations.set(key, nextPending);
}

export function getAnnotationRouteProjectIds(context: RealtimeContext): Set<string> {
  const projectIds = new Set<string>();
  if (context.currentProjectId) {
    projectIds.add(context.currentProjectId);
  }
  if (context.pathname === '/project/parse-flow') {
    const params = new URLSearchParams(context.search);
    for (const value of [params.get('parentProjectId'), params.get('projectId')]) {
      if (value) projectIds.add(value);
    }
    return projectIds;
  }
  const routeProjectId = context.pathname.match(/^\/project\/([^/]+)$/u)?.[1] ?? null;
  if (routeProjectId) {
    projectIds.add(routeProjectId);
  }
  return projectIds;
}

export function handleAnnotationRealtimeEvent(
  queryClient: QueryClient,
  event: RealtimeEvent,
  context: RealtimeContext
): void {
  if (event.topic !== 'annotations') return;
  const projectId = readString(event.payload.projectId);
  const screenplayId = readString(event.payload.screenplayId);
  if (!projectId || !screenplayId) return;
  if (!getAnnotationRouteProjectIds(context).has(projectId)) return;

  scheduleAnnotationRealtimeInvalidation(queryClient, event, projectId, screenplayId);
}

export function flushPendingAnnotationRealtimeInvalidationsForTest(): void {
  for (const key of pendingAnnotationInvalidations.keys()) {
    flushPendingAnnotationInvalidation(key);
  }
}
